/**
 * Input schemas for the infrastructure-domain callables — Power BI and
 * SharePoint setup / backfill / event-page provisioning. These are
 * admin-only maintenance callables run from the seed pipeline (or
 * manually during ops work); inputs are mostly toggles and overrides.
 */

import { z } from 'zod';

// ── SharePoint backfill ──

export const sharePointCollectionSchema = z.enum([
  'events',
  'sessions',
  'users',
  'eventRoster',
  'notifications',
  'surveys',
]);

export type SharePointCollection = z.infer<typeof sharePointCollectionSchema>;

export const backfillSharePointInputSchema = z
  .object({
    /** Which collections to backfill. Defaults to all. */
    collections: z.array(sharePointCollectionSchema).optional(),
    /** If true, clear all existing rows in the SharePoint lists before syncing. */
    clearFirst: z.boolean().optional(),
  })
  .strict();

export type BackfillSharePointInput = z.infer<
  typeof backfillSharePointInputSchema
>;

// ── SharePoint home page ──

export const setupSharePointHomePageInputSchema = z
  .object({
    /**
     * If true, delete the existing page (matched by `pageName`) before
     * re-authoring. Defaults to false (skip if present).
     */
    replace: z.boolean().optional(),
    /**
     * Override the target page filename. Defaults to `HomeBranded.aspx`.
     * Useful for safe first-test runs against `HomeBranded-TEST.aspx`.
     */
    pageName: z.string().optional(),
  })
  .strict();

export type SetupSharePointHomePageInput = z.infer<
  typeof setupSharePointHomePageInputSchema
>;

// ── SharePoint subscriptions ──

export const createSharePointSubscriptionsInputSchema = z
  .object({
    /** Optional override for the notification URL (e.g. ngrok tunnel for dev). */
    webhookUrl: z.string().optional(),
  })
  .strict();

export type CreateSharePointSubscriptionsInput = z.infer<
  typeof createSharePointSubscriptionsInputSchema
>;

// ── SharePoint setup (no input) ──

export const setupSharePointInputSchema = z.object({}).strict();
export type SetupSharePointInput = z.infer<typeof setupSharePointInputSchema>;

// ── SharePoint event pages backfill (no input) ──

export const backfillEventPagesInputSchema = z.object({}).strict();
export type BackfillEventPagesInput = z.infer<
  typeof backfillEventPagesInputSchema
>;

// ── Power BI setup ──

export const setupPowerBIInputSchema = z
  .object({
    /**
     * Whether to update the dataset schema if columns drift. Defaults to
     * true. Pass false to keep the existing schema and only refresh rows.
     */
    updateSchema: z.boolean().optional(),
  })
  .strict();

export type SetupPowerBIInput = z.infer<typeof setupPowerBIInputSchema>;

// ── Power BI backfill ──

export const backfillPowerBIInputSchema = z
  .object({
    /** Clear all existing rows in the push dataset before pushing. Default true. */
    clearFirst: z.boolean().optional(),
  })
  .strict();

export type BackfillPowerBIInput = z.infer<typeof backfillPowerBIInputSchema>;
